/**
 * PULSE SAVED ITEMS (2026-08-31)
 *
 * The heart in the nav, its count, and the panel behind it. Reads the SAME
 * localStorage key the heart on every Axel product card writes
 * (assets/pulse-cards2.js, `pulse-saved`) -- one store, two readers, never a
 * second list to keep in step.
 *
 * The panel is a structural twin of the cart drawer: same `.drawer pulse-cart`
 * classes and the same `.active` convention, so opening it is a class and the
 * whole Axel layout comes from pulse-sections.css. Its rows are built with the
 * cart's own `.sc__morerow` markup, which means each one gets a working
 * <pulse-cart-add> -- a custom element upgrades when it is inserted, so the
 * size picker and Add to bag defined in pulse-cart-axel.js need no wiring here.
 *
 * Rows are rendered on OPEN, not on load. The list changes while the customer
 * is elsewhere on the page (every card heart writes to it), and rendering late
 * means the panel can never show a stale list.
 */
(function () {
  var SAVED = 'pulse-saved';

  function readSaved() {
    try {
      var list = JSON.parse(localStorage.getItem(SAVED) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  function writeSaved(list) {
    try {
      localStorage.setItem(SAVED, JSON.stringify(list));
    } catch (e) {
      /* private mode: the list just does not persist, which is survivable */
    }
    document.dispatchEvent(new CustomEvent('pulse:saved-changed'));
  }

  var catalogue = null;
  var charts = {};
  function catalogueById() {
    if (catalogue) return catalogue;
    catalogue = {};
    var el = document.querySelector('[data-saved-catalog]');
    if (!el) return catalogue;
    try {
      /* The document is {s: charts, p: products} as of audit item 16
         (2026-09-06). `s` carries the two size charts ONCE -- new and original,
         keyed by US men's, each row [usw, uk, eu] -- so a variant only has to
         carry two numbers instead of two rendered sentences. A bare array is
         the pre-item-16 shape and is still read, so a cached page from either
         side of the push renders. */
      var doc = JSON.parse(el.textContent);
      var list = Array.isArray(doc) ? doc : doc.p || [];
      charts = (doc && doc.s) || {};
      list.forEach(function (p) {
        catalogue[String(p.i)] = p;
      });
    } catch (e) {
      /* a malformed map leaves an empty panel rather than a broken page */
    }
    return catalogue;
  }

  /* ---------- the count on the nav heart ---------- */
  function syncCount() {
    var n = readSaved().length;
    document.querySelectorAll('[data-pulse-saved-count]').forEach(function (el) {
      el.textContent = n;
      if (n === 0) el.setAttribute('hidden', '');
      else el.removeAttribute('hidden');
    });
    /* RED WHILE ANYTHING IS SAVED (Andre, 2026-08-31 21:37: "I would also like
       the wish list to turn red once the one is registered"). The class carries
       it rather than an inline style, because the nav heart is also recoloured
       white by pulse-split-hero.css when the bar sits over the hero image, and
       two scripts writing style.color would fight. */
    document.querySelectorAll('[data-pulse-saved-toggle]').forEach(function (btn) {
      btn.classList.toggle('is-saved', n > 0);
    });
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return {'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c];
    });
  }

  /* ONE SIZE, SAID IN FULL -- "Women's US 10 \u00b7 UK 8 \u00b7 EU 42".
     This is the string the picker shows AND the string the cart line carries as
     its visible `Size` property, so the panel and the customer's order cannot
     disagree.

     It used to be rendered twice per variant by snippets/pulse-size-label.liquid
     and shipped on every page of the site for a panel most visitors never open
     (audit item 16, 2026-09-06). It is now composed here from `v.n`, the
     `<usm>~<usw>` pair from that snippet's single `style: 'nums'` render, plus
     the size-chart row -- because UK exists ONLY in the chart, never on the
     variant, and the two charts disagree for the same US size.

     Composition order and separators mirror the snippet exactly: the scale word,
     then UK, then EU, each dropped when the chart has no value for it. The
     comparison is not by eye: scripts/tests/prove_size_labels.py computes both
     routes for every ACTIVE variant against the live charts.

     A variant the Liquid could not serve cheaply still arrives with `lm`/`lw`
     rendered in full, and those win. */
  var DOT = ' \u00b7 ';
  function sentence(word, us, row) {
    var out = word + us;
    if (row && row[1]) out += DOT + 'UK ' + row[1];
    if (row && row[2]) out += DOT + 'EU ' + row[2];
    return out;
  }
  function readings(v, p) {
    var pair = typeof v.n === 'string' && v.n.indexOf('~') !== -1 ? v.n.split('~') : null;
    if (!pair) {
      /* No pair: a gift card, or a variant with no usable SKU. Exactly the
         pre-item-16 fallback, and the same strings it produced. */
      var m = v.m, w = v.w;
      if (!m || !w) return { textW: v.t, textM: v.t, labelW: v.lw || v.t, labelM: v.lm || v.t };
      pair = [m, w];
    }
    var row = (charts[p && p.r] || {})[pair[0]];
    var lw = v.lw || sentence("Women's US ", pair[1], row);
    var lm = v.lm || sentence("Men's US ", pair[0], row);
    return { textW: lw, textM: lm, labelW: lw, labelM: lm };
  }

  /* ---------- one row, in the cart's own markup ---------- */
  function rowHTML(p) {
    var opts = '<option value="">Size</option>';
    (p.v || []).forEach(function (v) {
      var r = readings(v, p);
      /* Every variant in the catalogue is addable: a size sold out in this
         market is not listed at all (Andre, 2026-09-06), so the Liquid skips it
         and the old per-variant `a` flag was true on every row it ever wrote.
         The suffix survives only for a cached document from before item 16. */
      var avail = v.a !== false;
      var out = avail ? '' : ' — sold out';
      /* Both readings ride on the option and pulse-cart-axel.js picks one, the
         same contract the cart's own selects use -- so this picker follows the
         women's/men's switch instead of being stuck on men's. */
      opts +=
        '<option value="' + v.i + '"' + (avail ? '' : ' disabled') +
        ' data-text-w="' + esc(r.textW + out) + '"' +
        ' data-text-m="' + esc(r.textM + out) + '"' +
        ' data-label-w="' + esc(r.labelW) + '"' +
        ' data-label-m="' + esc(r.labelM) + '">' +
        esc(r.textW + out) + '</option>';
    });

    // image_url emits a PROTOCOL-RELATIVE url (//cdn.shopify.com/...). That is
    // correct on the storefront and breaks anywhere the page is not served over
    // http(s) -- a saved copy, a local harness -- where it resolves against the
    // file scheme. Pinning the scheme costs nothing and makes the two agree.
    var src = p.m && p.m.indexOf('//') === 0 ? 'https:' + p.m : p.m;

    var price = p.hw
      ? '<s class="sc__linewas">' + esc(p.w) + '</s><span class="sc__linenow">' + esc(p.p) + '</span>'
      : esc(p.p);

    return (
      '<div class="sc__morerow" data-saved-row="' + p.i + '">' +
        '<a href="' + esc(p.u) + '" class="sc__moremedia" tabindex="-1" aria-hidden="true">' +
          (src ? '<img src="' + esc(src) + '" alt="" loading="lazy">' : '') +
        '</a>' +
        '<div class="sc__morebodytext">' +
          '<div class="sc__linehead">' +
            '<div class="sc__linename">' +
              /* Browse rows, so both words go in and the site-wide CSS switch
                 picks one -- the same .cx2-w / .cx2-m pair the wall uses. */
              '<a href="' + esc(p.u) + '" class="sc__linetitle">' +
                '<span class="cx2-w">Women\'s</span><span class="cx2-m">Men\'s</span> ' + esc(p.t) +
              '</a>' +
            '</div>' +
            '<p class="sc__lineprice' + (p.hw ? ' sc__lineprice--sale' : '') + '">' + price + '</p>' +
          '</div>' +

          '<div class="sc__removewrap">' +
            '<button type="button" class="sc__remove" data-saved-remove="' + p.i + '"' +
            ' aria-label="Remove ' + esc(p.t) + ' from saved">Remove</button>' +
          '</div>' +
        '</div>' +
        '<pulse-cart-add class="sc__linectl sc__morectl">' +
          '<div class="sc__size">' +
            '<select class="sc__sizesel" data-add-select aria-label="Size for ' + esc(p.t) + '">' + opts + '</select>' +
            '<span class="sc__sizecaret" aria-hidden="true"></span>' +
          '</div>' +
          '<button type="button" class="sc__moreadd" data-add-button>Add to bag</button>' +
        '</pulse-cart-add>' +
      '</div>'
    );
  }

  class PulseSavedDrawer extends HTMLElement {
    connectedCallback() {
      /* WP4-I (2026-09-06). connectedCallback runs on EVERY reconnection, and this element is
         reconnected in the ordinary course of the page: the theme editor re-renders the header
         group, and anything that moves or re-appends the panel disconnects and reconnects it.
         Each run added another set of listeners -- including two on the DOCUMENT, which nothing
         ever removes -- so one Escape closed the panel twice and one Remove click deleted
         through two handlers. The properties below are cheap to re-read; the listeners are not,
         so only they are guarded. */
      this.body = this.querySelector('[data-saved-body]');
      this.empty = this.querySelector('[data-saved-empty]');

      if (this.__bound) { syncCount(); return; }
      this.__bound = true;

      this.querySelectorAll('[data-saved-close]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });
      this.addEventListener('keyup', (event) => {
        if (event.key === 'Escape') this.close();
      });
      /* On the document too, so Escape still works once focus has left the
         panel (audit T-23). */
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.classList.contains('active')) this.close();
      });
      // A row's Remove is delegated: rows are rebuilt on every open, so binding
      // each button would re-bind the same handler set every time.
      this.addEventListener('click', (event) => {
        var btn = event.target.closest('[data-saved-remove]');
        if (!btn) return;
        var id = btn.getAttribute('data-saved-remove');
        var list = readSaved().filter(function (x) {
          return String(x) !== String(id);
        });
        writeSaved(list);
        this.render();
      });

      document.addEventListener('pulse:saved-changed', syncCount);
      // Another tab saving something is worth reflecting; `storage` fires only
      // in the OTHER tabs, which is exactly the case the custom event misses.
      window.addEventListener('storage', function (event) {
        if (event.key === SAVED) syncCount();
      });
      syncCount();
    }

    render() {
      var map = catalogueById();
      // Filter to what the catalogue still knows about: a product can be
      // unpublished or deleted long after someone saved it, and a row built
      // from a missing entry would be a blank card linking nowhere.
      var items = readSaved()
        .map(function (id) { return map[String(id)]; })
        .filter(Boolean);

      this.body.innerHTML = items.map(rowHTML).join('');
      if (items.length) {
        this.empty.setAttribute('hidden', '');
        this.body.removeAttribute('hidden');
      } else {
        this.empty.removeAttribute('hidden');
        this.body.setAttribute('hidden', '');
      }
      syncCount();
    }

    open() {
      if (this.classList.contains('active')) return;
      this.render();
      // Same shape as cart-drawer.js: the class goes on next tick or the slide
      // does not always fire.
      setTimeout(() => this.classList.add('animate', 'active'));
      document.body.classList.add('overflow-hidden');
      var panel = this.querySelector('.drawer__inner');
      if (panel) setTimeout(() => {
        panel.focus();
        /* Contain focus while open (audit T-23): the whole element, so the
           Close button in the header is inside the loop. */
        if (typeof trapFocus === 'function') trapFocus(this, panel);
      }, 60);
    }

    close() {
      this.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.opener || null);
      else if (this.opener) this.opener.focus();
    }

    toggle(opener) {
      this.opener = opener || null;
      if (this.classList.contains('active')) this.close();
      else this.open();
    }
  }

  /* WP4-I (2026-09-06): defining a name twice throws a NotSupportedError that kills the rest of
     this file -- and this asset is loaded by more than one section, so a page carrying two of
     them ran the define twice. Ask first, the same guard snippets/pulse-size-drawer.liquid
     already uses for its own element. */
  if (!customElements.get('pulse-saved-drawer')) {
    customElements.define('pulse-saved-drawer', PulseSavedDrawer);
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    syncCount();
    document.querySelectorAll('[data-pulse-saved-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var drawer = document.querySelector('pulse-saved-drawer');
        // The cart and this panel are both full-height right-hand drawers, so
        // one closes the other rather than stacking two scrims.
        var cart = document.querySelector('cart-drawer');
        if (cart && cart.classList.contains('active') && typeof cart.close === 'function') cart.close();
        if (drawer) drawer.toggle(btn);
      });
    });
  });
})();
